import { useEffect, useState } from 'react';
import { Edit, Plus, Power, Search } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Pagination } from '../../../shared/components/Pagination';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { LOCATION_TYPE_LABELS, LOCATION_TYPE_OPTIONS, LocationType } from '../../../shared/lib/locations';
import { FEATURES } from '../../../shared/lib/permissions';
import { ActiveFilter, Location, mensagemDeErro } from '../api';
import { LocalidadeFormModal } from '../components/LocalidadeFormModal';
import { SituacaoBadge } from '../components/SituacaoBadge';
import { useLocalidades, useSituacaoLocalidade } from '../hooks';

const DEFAULT_PAGE_SIZE = 25;
const TODOS_OS_TIPOS = '';

/**
 * Cadastro de localidades (issue #300, RN01–RN06).
 *
 * A localidade é onde o ativo está alocado, independente do responsável
 * (RN07). Só as ativas ficam disponíveis para novas alocações (RN04); as
 * inativas continuam no histórico (RN11), por isso a ação é inativar, não
 * excluir.
 */
export default function LocalidadesPage() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState<ActiveFilter>('true');
  const [type, setType] = useState<LocationType | typeof TODOS_OS_TIPOS>(TODOS_OS_TIPOS);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [toggling, setToggling] = useState<Location | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setOffset(0), [debounced, active, type]);

  const localidades = useLocalidades({
    search: debounced || undefined,
    active,
    type: type || undefined,
    limit,
    offset,
  });
  const situacao = useSituacaoLocalidade();
  const items = localidades.data?.data ?? [];
  const count = localidades.data?.pagination.count ?? 0;

  function openForm(localidade: Location | null) {
    setEditing(localidade);
    setFormOpen(true);
  }

  async function confirmToggle() {
    if (!toggling) return;
    try {
      await situacao.mutateAsync({ id: toggling.id, active: !toggling.active });
      toast.success(
        toggling.active
          ? `Localização "${toggling.name}" inativada.`
          : `Localização "${toggling.name}" reativada.`,
      );
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação da localização.'));
    } finally {
      setToggling(null);
    }
  }

  function vinculo(localidade: Location): string {
    if (localidade.type === 'client') return localidade.clientName ?? 'Cliente não vinculado';
    if (localidade.type === 'company_unit') return localidade.companyName ?? '—';
    return '—';
  }

  return (
    <PageContainer
      title="Localidades"
      subtitle="Onde os ativos estão alocados: unidades, clientes, estoque, assistência técnica e demais locais parametrizados."
      breadcrumbs={[{ label: 'Cadastros' }, { label: 'Localidades' }]}
      action={
        <Can feature={FEATURES.masterDataLocalidades} action="create">
          <Button onClick={() => openForm(null)}>
            <Plus className="mr-2 h-5 w-5" />
            Nova localização
          </Button>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-md">
          <TextInput
            label="Buscar por código ou nome"
            icon={<Search className="h-5 w-5" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Tipo"
            options={[{ value: TODOS_OS_TIPOS, label: 'Todos os tipos' }, ...LOCATION_TYPE_OPTIONS]}
            value={type}
            onChange={(v) => setType(v as LocationType | typeof TODOS_OS_TIPOS)}
          />
        </div>
        <div className="min-w-[180px]">
          <AutocompleteSelect
            label="Situação"
            options={[
              { value: 'true', label: 'Ativas' },
              { value: 'false', label: 'Inativas' },
              { value: 'all', label: 'Todas' },
            ]}
            value={active}
            onChange={(v) => setActive(v as ActiveFilter)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {localidades.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando localidades…</p>
        ) : localidades.isError ? (
          <p className="p-6 text-sm text-red-600">
            {mensagemDeErro(localidades.error, 'Não foi possível carregar as localidades.')}
          </p>
        ) : items.length === 0 ? (
          <EmptyState
            title="Nenhuma localização encontrada"
            description="Ajuste a busca ou cadastre uma nova localização."
          />
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Vínculo</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((localidade) => (
                <tr key={localidade.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{localidade.code}</td>
                  <td className="px-4 py-3 text-gray-900">{localidade.name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {LOCATION_TYPE_LABELS[localidade.type] ?? localidade.type}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{vinculo(localidade)}</td>
                  <td className="px-4 py-3">
                    <SituacaoBadge active={localidade.active} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Can feature={FEATURES.masterDataLocalidades} action="edit">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openForm(localidade)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                          aria-label={`Editar ${localidade.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggling(localidade)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                          aria-label={
                            localidade.active
                              ? `Inativar ${localidade.name}`
                              : `Reativar ${localidade.name}`
                          }
                        >
                          <Power className="h-4 w-4" />
                        </button>
                      </div>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        offset={offset}
        limit={limit}
        count={count}
        itemLabel="localização"
        onChange={(next) => {
          setOffset(next.offset);
          setLimit(next.limit);
        }}
      />

      <LocalidadeFormModal
        open={formOpen}
        localidade={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(message) => {
          setFormOpen(false);
          toast.success(message);
        }}
      />

      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.active ? 'Inativar localização?' : 'Reativar localização?'}
        message={
          toggling?.active
            ? `A localização "${toggling?.name}" deixará de aparecer para novas alocações. O histórico de movimentações é preservado.`
            : `A localização "${toggling?.name}" voltará a ficar disponível para novas alocações.`
        }
        confirmLabel={toggling?.active ? 'Inativar' : 'Reativar'}
        danger={toggling?.active}
        busy={situacao.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggling(null)}
      />
    </PageContainer>
  );
}
